import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import type { MobileZoom } from '../../utils/canvas/canvas';

interface UseTouchPanProps {
  isMobile: boolean;
  mobileZoom: MobileZoom;
  setMobileZoom: React.Dispatch<React.SetStateAction<MobileZoom>>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

interface UseTouchPanReturn {
  isPanning: boolean;
  setIsPanning: React.Dispatch<React.SetStateAction<boolean>>;
  lastPanPosition: { x: number; y: number };
  setLastPanPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  handleTouchStart: (event: TouchEvent) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: (event: TouchEvent) => void;
}

export const useTouchPan = ({
  isMobile,
  mobileZoom,
  setMobileZoom,
  canvasRef,
}: UseTouchPanProps): UseTouchPanReturn => {
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPosition, setLastPanPosition] = useState({ x: 0, y: 0 });

  const canvasBoundsRef = useRef<DOMRect | null>(null);
  const lastScaleRef = useRef<number>(1);

  const getMaxTranslateBounds = useCallback(() => {
    if (!canvasBoundsRef.current || mobileZoom.scale <= 1) {
      return { maxTranslateX: 0, maxTranslateY: 0 };
    }

    const { width, height } = canvasBoundsRef.current;
    return {
      maxTranslateX: (width * (mobileZoom.scale - 1)) / 2,
      maxTranslateY: (height * (mobileZoom.scale - 1)) / 2,
    };
  }, [mobileZoom.scale]);

  const clampTranslate = useCallback((value: number, max: number) => {
    return Math.max(-max, Math.min(max, value));
  }, []);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 1 && mobileZoom.scale > 1) {
        // Single finger pan when zoomed in
        event.preventDefault();
        const touch = event.touches[0];
        setLastPanPosition({ x: touch.clientX, y: touch.clientY });
        setIsPanning(true);
        setMobileZoom((prev) => ({ ...prev, isZooming: false }));
      }
    },
    [isMobile, mobileZoom.scale, setMobileZoom]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 1 && isPanning && mobileZoom.scale > 1) {
        // Single finger pan when zoomed in
        event.preventDefault();

        const touch = event.touches[0];
        const deltaX = touch.clientX - lastPanPosition.x;
        const deltaY = touch.clientY - lastPanPosition.y;

        const canvas = canvasRef.current;
        if (canvas) {
          if (!canvasBoundsRef.current || lastScaleRef.current !== mobileZoom.scale) {
            canvasBoundsRef.current = canvas.getBoundingClientRect();
            lastScaleRef.current = mobileZoom.scale;
          }

          const { maxTranslateX, maxTranslateY } = getMaxTranslateBounds();

          const newTranslateX = clampTranslate(mobileZoom.translateX + deltaX, maxTranslateX);
          const newTranslateY = clampTranslate(mobileZoom.translateY + deltaY, maxTranslateY);

          setMobileZoom((prev) => ({
            ...prev,
            translateX: newTranslateX,
            translateY: newTranslateY,
          }));

          setLastPanPosition({ x: touch.clientX, y: touch.clientY });
        }
      }
    },
    [isMobile, isPanning, mobileZoom, lastPanPosition, canvasRef, setMobileZoom, getMaxTranslateBounds, clampTranslate]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 0) {
        setIsPanning(false);
        canvasBoundsRef.current = null;
      }
    },
    [isMobile]
  );

  return {
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    isPanning,
    lastPanPosition,
    setIsPanning,
    setLastPanPosition,
  };
};
